/**
 * usePracticeBasePath - Hook per percorso base documenti pratiche
 *
 * Persiste in IndexedDB per ricordare l'ultima cartella selezionata.
 */
import { useState, useCallback, useEffect } from 'react';
import { openDB } from 'idb';

const DB_NAME = 'studio-gori-filesystem';
const DB_VERSION = 2;
const BASE_PATH_ID = 'practice-base-path';

interface FileSystemDirectoryHandleExt extends FileSystemDirectoryHandle {
  queryPermission?(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission?(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<FileSystemDirectoryHandle>;
}

interface StoredHandle {
  id: string;
  handle: FileSystemDirectoryHandleExt;
  path: string;
  createdAt: number;
}

interface FileSystemDB {
  handles: {
    key: string;
    value: StoredHandle;
  };
}

async function getDB() {
  return openDB<FileSystemDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('handles')) {
        db.createObjectStore('handles', { keyPath: 'id' });
      }
    },
  });
}

async function saveHandle(handle: FileSystemDirectoryHandleExt, path: string) {
  const db = await getDB();
  await db.put('handles', {
    id: BASE_PATH_ID,
    handle,
    path,
    createdAt: Date.now(),
  });
}

async function loadHandle(): Promise<FileSystemDirectoryHandleExt | null> {
  try {
    const db = await getDB();
    const stored = await db.get('handles', BASE_PATH_ID);
    if (!stored?.handle) return null;

    const handle = stored.handle as FileSystemDirectoryHandleExt;
    const permission = await handle.queryPermission?.({ mode: 'readwrite' });
    if (permission === 'granted') return handle;

    return null;
  } catch {
    return null;
  }
}

async function hasStoredHandle(): Promise<boolean> {
  try {
    const db = await getDB();
    const stored = await db.get('handles', BASE_PATH_ID);
    return !!stored?.handle;
  } catch {
    return false;
  }
}

export function usePracticeBasePath() {
  const [baseHandle, setBaseHandle] = useState<FileSystemDirectoryHandleExt | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasSavedPath, setHasSavedPath] = useState(false);
  const isSupported = typeof window !== 'undefined' && 'showDirectoryPicker' in window && !window.isElectron;

  const loadBasePath = useCallback(async () => {
    if (!isSupported) {
      setIsLoading(false);
      return;
    }
    try {
      const exists = await hasStoredHandle();
      setHasSavedPath(exists);
      const handle = await loadHandle();
      setBaseHandle(handle);
      const db = await getDB();
      const stored = await db.get('handles', BASE_PATH_ID);
      setDisplayName(stored?.path || (handle ? handle.name : ''));
    } catch {
      setBaseHandle(null);
      setDisplayName('');
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  useEffect(() => {
    loadBasePath();
  }, [loadBasePath]);

  /** Richiede il permesso sull'handle salvato. DEVE essere chiamato da un click utente. */
  const requestPermissionAndReload = useCallback(async (): Promise<boolean> => {
    try {
      const db = await getDB();
      const stored = await db.get('handles', BASE_PATH_ID);
      if (!stored?.handle) return false;
      const handle = stored.handle as FileSystemDirectoryHandleExt;
      const result = await handle.requestPermission?.({ mode: 'readwrite' });
      if (result === 'granted') {
        setBaseHandle(handle);
        setDisplayName(stored.path || handle.name);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const selectBasePath = useCallback(async (): Promise<{ handle: FileSystemDirectoryHandleExt; displayName: string } | null> => {
    if (!isSupported || !window.showDirectoryPicker) return null;
    try {
      const handle = (await window.showDirectoryPicker({
        id: 'practice-base-picker',
        mode: 'readwrite',
      })) as FileSystemDirectoryHandleExt;

      await saveHandle(handle, handle.name);
      setBaseHandle(handle);
      setDisplayName(handle.name);
      return { handle, displayName: handle.name };
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return null;
      throw err;
    }
  }, [isSupported]);

  /** Salva un handle già ottenuto come base path (senza aprire il picker) */
  const selectBasePathWithHandle = useCallback(async (handle: FileSystemDirectoryHandleExt) => {
    await saveHandle(handle, handle.name);
    setBaseHandle(handle);
    setDisplayName(handle.name);
    setHasSavedPath(true);
  }, []);

  const clearBasePath = useCallback(async () => {
    try {
      const db = await getDB();
      await db.delete('handles', BASE_PATH_ID);
      setBaseHandle(null);
      setDisplayName('');
    } catch {
      setBaseHandle(null);
      setDisplayName('');
    }
  }, []);

  return {
    baseHandle,
    displayName,
    isLoading,
    isSupported,
    hasSavedPath,
    selectBasePath,
    selectBasePathWithHandle,
    loadBasePath,
    clearBasePath,
    requestPermissionAndReload,
  };
}
